import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import { Sparkles, X, Gift } from "lucide-react";

/**
 * Celebratory banner that surfaces after a friend signs up via the user's
 * referral link. Polls /api/notifications for unread `referral_signup`
 * events and renders a dismissable amber-gold banner. Multiple referrals
 * collapse into one banner ("3 friends joined — that's +150 credits!").
 *
 * Acks / dismiss behavior:
 *   • The "Refer more friends" button opens the existing referral modal
 *     (same trigger the sidebar uses) and silently marks the notifs read.
 *   • The X button just marks them read so the banner doesn't reappear
 *     on the next page load.
 *   • A localStorage key prevents the banner from re-flashing within the
 *     same session if the user has already dismissed it (defensive against
 *     polling races).
 */
export default function ReferralCelebrationBanner() {
  const [items, setItems] = useState([]);
  const [dismissedThisSession, setDismissedThisSession] = useState(false);

  const loadReferralNotifs = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/notifications?limit=30`, { withCredentials: true });
      const notifs = res.data?.notifications || [];
      const unreadReferrals = notifs.filter(
        n => n.event === "referral_signup" && !n.read,
      );
      setItems(unreadReferrals);
    } catch {
      // Silent — banner is best-effort enrichment, never block the page.
    }
  }, []);

  useEffect(() => {
    if (dismissedThisSession) return undefined;
    loadReferralNotifs();
    // Refresh on the standard notifications:refresh signal (fired after
    // billing actions, generations, etc.) plus a 60s safety net.
    const onRefresh = () => loadReferralNotifs();
    window.addEventListener("notifications:refresh", onRefresh);
    const t = setInterval(loadReferralNotifs, 60000);
    return () => {
      window.removeEventListener("notifications:refresh", onRefresh);
      clearInterval(t);
    };
  }, [dismissedThisSession, loadReferralNotifs]);

  const markRead = async () => {
    if (!items.length) return;
    setDismissedThisSession(true);
    setItems([]);
    try {
      await axios.post(
        `${API}/notifications/mark-read`,
        { ids: items.map(i => i.id).filter(Boolean) },
        { withCredentials: true },
      );
      window.dispatchEvent(new Event("notifications:refresh"));
    } catch {
      // non-blocking
    }
  };

  const openReferralModal = () => {
    // Sidebar already wires this same custom event to ReferralModal.
    window.dispatchEvent(new Event("referral:open"));
    markRead();
  };

  if (!items.length || dismissedThisSession) return null;

  // Each referral grants 50 credits. Sum them for the celebratory line.
  const count = items.length;
  const creditsEarned = items.reduce((sum, n) => {
    // Notif message is "+50 credits — thanks for spreading the word."
    // We don't have a structured `delta` field, so default to 50/each.
    return sum + 50;
  }, 0);

  const friendCopy = count === 1 ? "A friend just joined" : `${count} friends just joined`;
  const creditCopy = count === 1
    ? `+${creditsEarned} credits added to your balance`
    : `That's +${creditsEarned} credits added to your balance`;

  return (
    <div
      className="relative overflow-hidden rounded-xl border border-amber-200 bg-gradient-to-r from-amber-50 via-yellow-50 to-orange-50 px-5 py-4 mb-5 shadow-sm"
      data-testid="referral-celebration-banner"
    >
      {/* decorative sparkle accent */}
      <div className="absolute -top-3 -right-3 opacity-20 pointer-events-none">
        <Sparkles className="w-20 h-20 text-amber-400" strokeWidth={1} />
      </div>

      <div className="relative flex items-start gap-3">
        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center shadow-inner">
          <Gift className="w-5 h-5 text-amber-700" />
        </div>

        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-amber-900 leading-snug">
            🎉 {friendCopy} via your referral link!
          </p>
          <p className="text-xs text-amber-800 mt-0.5">
            {creditCopy}. Keep sharing — every signup earns both of you 50 credits.
          </p>
          <div className="flex items-center gap-2 mt-2.5">
            <button
              onClick={openReferralModal}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md bg-amber-600 text-white hover:bg-amber-700 transition-colors"
              data-testid="referral-banner-share-btn"
            >
              <Sparkles className="w-3 h-3" />Refer more friends
            </button>
            <button
              onClick={markRead}
              className="text-xs font-medium text-amber-700 hover:text-amber-900"
              data-testid="referral-banner-dismiss-btn"
            >
              Got it
            </button>
          </div>
        </div>

        <button
          onClick={markRead}
          className="flex-shrink-0 text-amber-500 hover:text-amber-800 transition-colors p-1"
          aria-label="Dismiss"
          data-testid="referral-banner-close-btn"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
